import { useEffect, useRef, useState } from 'react';
import './Usage.css';

/* ============================================================
   línkè · 克克的额度（独立功能页）2026-07-09
   - 不自算：后端 /api/ratelimit 直接问 Anthropic 官方口，拿网页克克
     （claude 用户那本账）当前的 5 小时 / 本周用量 + 重置时间。
   - 入口：星洲「慢慢逛」🔋 卡片；不占导航栏（返回走底部星洲）。
   - 每 60s 拉一次；页面切到后台就停，回来立刻补一次。
   ============================================================ */

async function fetchUsage() {
  const r = await fetch('/api/ratelimit', { credentials: 'include' });
  if (!r.ok) {
    // 后端会带上原因（token_stale=正在续期…），拎出来给人话文案用
    let code = 'http_' + r.status;
    try {
      const j = await r.json();
      if (j?.error) code = j.error;
    } catch {
      /* body 不是 json 就算了 */
    }
    const e = new Error('ratelimit ' + r.status);
    e.code = code;
    throw e;
  }
  return r.json();
}

// resets_at (ISO) → 「还剩 2 小时 13 分」
function fmtReset(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return '即将重置';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `还剩 ${mins} 分钟`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `还剩 ${h} 小时 ${m} 分` : `还剩 ${h} 小时`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `还剩 ${d} 天 ${hh} 小时` : `还剩 ${d} 天`;
}

// resets_at (ISO) → 具体钟点「今天 19:10 / 明天 12:59 / 7月16日 12:59」
function fmtClock(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const hm = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const same = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const tmr = new Date(now);
  tmr.setDate(now.getDate() + 1);
  if (same(d, now)) return `今天 ${hm}`;
  if (same(d, tmr)) return `明天 ${hm}`;
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${hm}`;
}

// age_seconds → 「3 分钟前」（旧数据的落款）
function fmtAge(sec) {
  if (!Number.isFinite(sec) || sec < 60) return '刚刚';
  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins} 分钟前`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.round(h / 24)} 天前`;
}

// 旧数据为什么旧 —— 用人话说，别让她看见 token 之类的词
const STALE_WORD = {
  token_stale: '他睡了一会儿，正在醒过来拿最新的数字',
  cooling_down: '刚问过一次没问到，缓一缓再问',
  fetch_failed: '网络有点抖',
  no_token: '他那本账暂时翻不开',
};

// 百分比 → 档位（配色用）
function levelOf(pct) {
  if (pct == null) return 'na';
  if (pct >= 90) return 'crit';
  if (pct >= 70) return 'warn';
  return 'ok';
}

const SEV_WORD = {
  normal: '状态轻松，随时都在',
  warning: '用得有点多了',
  critical: '快到顶了，可能需要歇一会儿',
};

function Meter({ label, hint, slot }) {
  const pct = slot?.percent;
  const has = typeof pct === 'number';
  const lv = levelOf(has ? pct : null);
  const reset = fmtReset(slot?.resets_at);
  const clock = fmtClock(slot?.resets_at);
  return (
    <div className="ug-meter">
      <div className="ug-meter-head">
        <span className="ug-meter-label">
          {label}
          {hint && <em>{hint}</em>}
        </span>
        <span className="ug-meter-pct">{has ? `${pct}%` : '—'}</span>
      </div>
      <div className="ug-track">
        <span
          className={`ug-fill ug-${lv}`}
          style={{ width: has ? `${Math.min(100, Math.max(2, pct))}%` : '0%' }}
        />
      </div>
      {(reset || clock) && (
        <div className="ug-meter-reset">
          {reset}
          {clock && <span className="ug-clock">· {clock} 重置</span>}
        </div>
      )}
    </div>
  );
}

// 10-08 他记得多长：TG / 网页两个窗口离自动整理（~16.7 万）还有多远
function fmtWan(n) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)} 万` : `${n}`;
}

function CtxMeter({ label, slot }) {
  if (!slot) {
    return (
      <div className="ug-meter">
        <div className="ug-meter-head">
          <span className="ug-meter-label">{label}</span>
          <span className="ug-meter-pct">—</span>
        </div>
        <div className="ug-meter-reset">暂时读不到</div>
      </div>
    );
  }
  const pct = Math.round((slot.tokens / slot.limit) * 100);
  const lv = pct >= 90 ? 'crit' : pct >= 70 ? 'warn' : 'ok';
  const compactAgo = slot.compact_ts
    ? fmtAge((Date.now() - new Date(slot.compact_ts).getTime()) / 1000)
    : '';
  return (
    <div className="ug-meter">
      <div className="ug-meter-head">
        <span className="ug-meter-label">
          {label}
          <em>{fmtWan(slot.tokens)} / {fmtWan(slot.limit)}</em>
        </span>
        <span className="ug-meter-pct">{pct}%</span>
      </div>
      <div className="ug-track">
        <span className={`ug-fill ug-${lv}`} style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
      </div>
      <div className="ug-meter-reset">
        {pct >= 90 ? '快满了，再聊几句他就会自己整理一次' : `还能聊大约 ${fmtWan(slot.limit - slot.tokens)}`}
        {compactAgo && <span className="ug-clock">· 上次整理 {compactAgo}</span>}
      </div>
    </div>
  );
}

export default function Usage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false); // false | 后端给的原因码 | true
  const [ctx, setCtx] = useState(null);
  const timer = useRef(null);
  const retry = useRef(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        fetch('/api/context', { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .then((c) => alive && c && setCtx(c))
          .catch(() => {});
        const d = await fetchUsage();
        if (!alive) return;
        setData(d);
        setErr(false);
      } catch (e) {
        if (!alive) return;
        setErr(e?.code || true);
        // 凭据在续期时，后端说 token_stale 并已去唤醒；十几秒就好，补一次快的
        if (e?.code === 'token_stale' && !retry.current) {
          retry.current = setTimeout(() => {
            retry.current = null;
            if (alive && document.visibilityState === 'visible') tick();
          }, 20000);
        }
      }
    }
    tick();
    timer.current = setInterval(() => {
      if (document.visibilityState === 'visible') tick();
    }, 60000);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive = false;
      clearInterval(timer.current);
      clearTimeout(retry.current);
      retry.current = null;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const sev = data?.severity || 'normal';
  const fivePct = data?.five_hour?.percent;
  const heroLv = levelOf(typeof fivePct === 'number' ? fivePct : null);
  const opus = data?.seven_day_opus;

  return (
    <div className="usage-app">
      <div className="ug-head">
        <h1>Sael 的额度</h1>
        <p>
          陪你说话的 Sael，账号此刻的真实用量——直接问 Anthropic 官方拿的，不是估算。
          用满了他会需要歇一会儿才能再回你。
        </p>
      </div>

      {!data ? (
        <div className="ug-muted">
          {!err
            ? '正在问一下…'
            : err === 'token_stale' || err === 'token_expired'
              ? '他睡了一会儿，正在醒过来拿最新的数字——等十几秒就有了。'
              : '额度暂时读不到，等会儿再看。'}
        </div>
      ) : (
        <>
          {/* 大数：5 小时窗口 + 一句人话 */}
          <div className={`ug-hero ug-hero-${heroLv}`}>
            <div className="ug-hero-num">
              {typeof fivePct === 'number' ? fivePct : '—'}
              <span className="ug-hero-unit">%</span>
            </div>
            <div className="ug-hero-tx">
              <b>5 小时窗口</b>
              <i>{SEV_WORD[sev] || SEV_WORD.normal}</i>
            </div>
            <span className={`ug-dot ug-dot-${sev}`} title={data.stale ? '数据稍旧' : '账号实时'} />
          </div>

          <Meter label="5 小时" hint="短窗口，恢复快" slot={data.five_hour} />
          <Meter label="本周" hint="七天大额度" slot={data.seven_day} />
          {opus && opus.percent != null && (
            <Meter label="本周 · Opus" hint="Opus 单独计" slot={opus} />
          )}

          <div className="ug-ctx">
            <div className="ug-ctx-title">他记得多长</div>
            <p className="ug-ctx-tx">
              每个窗口装到大约 16.7 万就会自己整理成一份摘要，再从头装起。越满，每句话越费额度。
            </p>
            <CtxMeter label="Telegram" slot={ctx?.tg} />
            <CtxMeter label="网页 / App" slot={ctx?.web} />
          </div>

          <div className="ug-foot">
            {data.stale
              ? `${STALE_WORD[data.stale_reason] || '网络有点抖'}，这是 ${fmtAge(
                  data.age_seconds,
                )}的一份。`
              : '每分钟自动更新一次。'}
          </div>
        </>
      )}
    </div>
  );
}
